/**
 * Fauna behaviour (docs 03/06): one tick of every fish in the water — damage
 * over time, boss clockwork, the Cloaked metronome, pursuit, biting, and the
 * walk down the flow field.
 *
 * Pure functions over a narrow host, like combat and economy (doc 12 §Code
 * health). The Sim owns the ordering and the consequences (a destroyed
 * structure dirties the flow field); this file owns what a fish *does*.
 */

import { DT, TICK_RATE } from './const.ts';
import { PLACEABLE } from './defs.ts';
import { structCentre } from './economy.ts';
import type { FlowField } from './flowfield.ts';
import { SimGrid, T_ROCK } from './grid.ts';
import { SPECIES, type AuraRule, type SpeciesDef } from './species.ts';
import type { Enemy, SimEvent, SimState, Structure } from './types.ts';

/** What fauna needs from the sim — nothing more. */
export interface FaunaHost {
  state: SimState;
  grid: SimGrid;
  flow: FlowField;
  emit(ev: SimEvent): void;
  byId(id: number): Structure | undefined;
  /** Applies damage and returns what actually landed (doc 17 §7 numbers). */
  hurt(e: Enemy, raw: number, def: string): number;
  /** Ruin it, dirty the flow field, end the run if it was the Core (doc 03). */
  destroyStructure(target: Structure): void;
}

/** One tick for every enemy on the map. */
export function updateFauna(host: FaunaHost): void {
  const s = host.state;
  const n = host.grid.size;
  const singers = s.enemies.filter((e) => e.hp > 0 && SPECIES[e.species]!.aura !== null);
  const frontDist = singers.length > 0 ? frontLine(host) : 0;

  for (const e of s.enemies) {
    const sp = SPECIES[e.species]!;

    // damage over time (doc 05: bleed, burn) — still the applier's XP
    if (e.bleedUntil > s.tick) host.hurt(e, e.bleedDps * DT, e.bleedSrc);
    if (e.burnUntil > s.tick) host.hurt(e, e.burnDps * DT, e.burnSrc);
    if (e.hp <= 0) continue; // reaped after this pass

    // Doc 06 Regen: it heals every second, and any single hit of 25 or more
    // stops that for two. The Mother of Threads adds her nodes on top.
    const regen = sp.regen + threadRegen(host, e, sp);
    if (regen > 0 && e.regenPauseUntil <= s.tick && e.hp < e.maxHp) {
      e.hp = Math.min(e.maxHp, e.hp + regen * DT);
    }
    // and a Chorister mends whatever it is standing behind (doc 06 aura)
    const aura = auraOn(e, singers);
    if (aura && e.hp < e.maxHp) e.hp = Math.min(e.maxHp, e.hp + aura.healPerSecond * DT);

    // the Pale Lure's metronome: 3 s hidden, 2 s not, on the ocean's own clock
    if (sp.cloakCycle) {
      const period = (sp.cloakCycle.cloakedSeconds + sp.cloakCycle.visibleSeconds) * TICK_RATE;
      if (s.tick % period >= sp.cloakCycle.cloakedSeconds * TICK_RATE) {
        e.revealedUntil = Math.max(e.revealedUntil, s.tick + 1);
      }
    }

    if (!runClockwork(host, e, sp)) continue; // submerged: nothing else happens
    if (e.stunUntil > s.tick) continue; // stunned: no walking, no chewing

    // chewing?
    if (e.attackTargetId >= 0) {
      const target = host.byId(e.attackTargetId);
      if (!target || target.ruin) {
        e.attackTargetId = -1;
      } else {
        strike(host, e, sp, target);
        continue;
      }
    }

    // effective speed: slows stack multiplicatively; enrage is a boss rule
    let speed = e.speed;
    if (e.slowUntil > s.tick) speed *= 1 - e.slowPct;
    if (e.bleedUntil > s.tick && e.bleedSlowPct > 0) speed *= 1 - e.bleedSlowPct;
    if (sp.enrage && e.hp < e.maxHp * sp.enrage.belowHpFraction) speed *= sp.enrage.speedMult;
    if (aura) speed *= 1 + aura.speedBonus;

    // a singer never leads: it holds `standoff` tiles behind the front (doc 06)
    if (sp.aura && distToCore(host, e) < frontDist + sp.aura.standoff) continue;

    // saboteur (doc 03/06): hunt the preferred class instead of the Core.
    // A juggernaut hunts the Core itself and eats the maze on the way (doc 06).
    if (sp.behavior === 'saboteur' && pursue(host, e, sp, speed, sabotageGoal(host, e, sp))) continue;
    if (sp.bite && pursue(host, e, sp, speed, host.byId(s.coreId))) continue;

    walkFlowField(host, e, speed, n);
  }
}

/**
 * Boss clockwork (doc 06 gimmicks): the grab, the escort pulse, the breach
 * dive. Returns false when the fish is submerged and this tick is over for it.
 */
function runClockwork(host: FaunaHost, e: Enemy, sp: SpeciesDef): boolean {
  const s = host.state;
  if (sp.grab && e.gimmickTick >= 0 && s.tick >= e.gimmickTick) {
    grabTower(host, e, sp);
    e.gimmickTick = s.tick + sp.grab.everySeconds * TICK_RATE;
  }
  // the escort runs on the SECOND clock, because the Sovereign needs both
  if (sp.escort && e.gimmickTick2 >= 0 && s.tick >= e.gimmickTick2) {
    const gated = sp.escort.below === undefined || e.hp <= e.maxHp * sp.escort.below;
    if (gated) {
      for (const g of sp.escort.spawn) {
        for (let k = 0; k < g.count; k++) {
          s.pendingSpawns.push({
            tick: s.tick + k * 6,
            species: g.species,
            waveId: e.waveId,
            edge: 'e',
            along: 0.5,
            atX: e.x,
            atY: e.y,
          });
        }
      }
      host.emit({ type: 'lure', name: sp.name, x: e.x, y: e.y });
    }
    e.gimmickTick2 = s.tick + sp.escort.everySeconds * TICK_RATE;
  }
  if (sp.dive && e.gimmickTick >= 0 && s.tick >= e.gimmickTick) {
    startDive(host, e, sp);
    e.gimmickTick = s.tick + sp.dive.everySeconds * TICK_RATE;
  }
  if (sp.threads && e.gimmickTick >= 0 && s.tick >= e.gimmickTick) {
    dropThread(host, e, sp);
    e.gimmickTick = s.tick + sp.threads.everySeconds * TICK_RATE;
  }
  if (sp.pulse && e.gimmickTick >= 0 && s.tick >= e.gimmickTick) {
    pressurePulse(host, e, sp);
    const hastened = e.hp <= e.maxHp * sp.pulse.hastenBelow;
    e.gimmickTick = s.tick + (hastened ? sp.pulse.hastenedSeconds : sp.pulse.everySeconds) * TICK_RATE;
  }
  return e.divingUntil <= s.tick;
}

/**
 * Doc 06 (Mother of Threads): she leaves a node behind, and every node still
 * standing near her heals her. The exam is split attention — the boss bar will
 * not move until the things around it are dealt with.
 */
function dropThread(host: FaunaHost, e: Enemy, sp: SpeciesDef): void {
  const t = sp.threads!;
  const s = host.state;
  const live = s.enemies.filter((x) => x.hp > 0 && x.species === t.species && x.waveId === e.waveId).length;
  if (live >= t.count) return; // six is six
  s.pendingSpawns.push({
    tick: s.tick + 1,
    species: t.species,
    waveId: e.waveId,
    edge: 'e',
    along: 0.5,
    atX: e.x,
    atY: e.y,
  });
  host.emit({ type: 'thread', x: e.x, y: e.y });
}

/** How much a boss's own nodes are giving back this tick. */
function threadRegen(host: FaunaHost, e: Enemy, sp: SpeciesDef): number {
  const t = sp.threads;
  if (!t) return 0;
  const r2 = t.radius * t.radius;
  let alive = 0;
  for (const other of host.state.enemies) {
    if (other.hp <= 0 || other.species !== t.species || other.waveId !== e.waveId) continue;
    if ((other.x - e.x) * (other.x - e.x) + (other.y - e.y) * (other.y - e.y) <= r2) alive++;
  }
  return alive * t.regenPer;
}

/**
 * Doc 06 (The Hadal Sovereign): the Pressure Pulse puts out every tower that
 * runs on the grid. The exam it sets is "did you keep ammo towers around, or
 * did you go full laser and pray" — so it deliberately leaves the fed ones be.
 */
function pressurePulse(host: FaunaHost, e: Enemy, sp: SpeciesDef): void {
  const s = host.state;
  const until = s.tick + Math.round(sp.pulse!.disableSeconds * TICK_RATE);
  let hit = 0;
  for (const st of s.structures) {
    const def = PLACEABLE[st.def];
    if (!def || st.ruin || def.kind !== 'tower' || def.power >= 0) continue;
    st.disabledUntil = Math.max(st.disabledUntil, until);
    st.targetId = -1;
    hit++;
  }
  host.emit({ type: 'pulse', name: sp.name, x: e.x, y: e.y, towers: hit });
}

/** The strongest aura covering this enemy, or null. A singer never buffs itself. */
function auraOn(e: Enemy, singers: Enemy[]): AuraRule | null {
  let best: AuraRule | null = null;
  for (const singer of singers) {
    if (singer.id === e.id) continue;
    const a = SPECIES[singer.species]!.aura!;
    const dx = singer.x - e.x;
    const dy = singer.y - e.y;
    if (dx * dx + dy * dy > a.radius * a.radius) continue;
    if (!best || a.healPerSecond > best.healPerSecond) best = a;
  }
  return best;
}

function distToCore(host: FaunaHost, e: Enemy): number {
  const core = host.byId(host.state.coreId);
  if (!core) return 0;
  const c = structCentre(core);
  return Math.sqrt((c.x - e.x) * (c.x - e.x) + (c.y - e.y) * (c.y - e.y));
}

/** How far in the closest non-support fish has got — the front line. */
function frontLine(host: FaunaHost): number {
  let best = Infinity;
  for (const e of host.state.enemies) {
    if (e.hp <= 0 || SPECIES[e.species]!.aura) continue;
    best = Math.min(best, distToCore(host, e));
  }
  return Number.isFinite(best) ? best : 0;
}

/**
 * The breach dive (doc 06 Leviathan Calf): it goes under, is untargetable for
 * two seconds, and comes up `tiles` further along its own straight line to the
 * Core. The jump happens at the START of the dive — nobody can see it travel,
 * and doing it here keeps the resurface out of the state as a second clock.
 */
function startDive(host: FaunaHost, e: Enemy, sp: SpeciesDef): void {
  const s = host.state;
  const dive = sp.dive!;
  e.divingUntil = s.tick + Math.round(dive.seconds * TICK_RATE);
  e.attackTargetId = -1;
  host.emit({ type: 'dive', name: sp.name, x: e.x, y: e.y });

  const core = host.byId(s.coreId);
  if (!core) return;
  const c = structCentre(core);
  const dx = c.x - e.x;
  const dy = c.y - e.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1) return;
  // advance tile by tile so it never surfaces inside a rock or a building
  const nsize = host.grid.size - 1;
  let x = e.x;
  let y = e.y;
  for (let k = 0; k < dive.tiles; k++) {
    const nx = Math.max(0, Math.min(nsize, x + dx / len));
    const ny = Math.max(0, Math.min(nsize, y + dy / len));
    const ti = Math.round(nx);
    const tj = Math.round(ny);
    if (!host.grid.inBounds(ti, tj)) break;
    if (host.grid.terrain[host.grid.idx(ti, tj)] === T_ROCK) break;
    const occ = host.grid.occ[host.grid.idx(ti, tj)]!;
    const blocker = occ >= 0 ? host.byId(occ) : undefined;
    if (blocker && !blocker.ruin) break;
    x = nx;
    y = ny;
  }
  e.x = x;
  e.y = y;
}

/** Old Snapjaw: grab the nearest live tower, disable it (doc 06). */
function grabTower(host: FaunaHost, e: Enemy, sp: SpeciesDef): void {
  let best: Structure | undefined;
  let bestD = Infinity;
  for (const st of host.state.structures) {
    if (st.kind !== 'tower' || st.ruin) continue;
    const c = structCentre(st);
    const d = (c.x - e.x) * (c.x - e.x) + (c.y - e.y) * (c.y - e.y);
    if (d < bestD) {
      bestD = d;
      best = st;
    }
  }
  if (!best) return;
  best.disabledUntil = host.state.tick + sp.grab!.disableSeconds * TICK_RATE;
  best.targetId = -1;
  host.emit({ type: 'grab', towerId: best.id, bossName: sp.name });
}

/**
 * One attack on the structure this fish has hold of. Three flavours:
 * teeth (dps), acid at four tiles (doc 03 ranged), and a jaw that takes one
 * enormous bite on a clock and treats a wall as a door (doc 06 Leviathan).
 */
function strike(host: FaunaHost, e: Enemy, sp: SpeciesDef, target: Structure): void {
  const s = host.state;
  // a Cloaked fish that is busy eating is briefly visible (doc 06 Lanternjaw)
  if (sp.cloaked && sp.uncloakOnAttack) {
    e.revealedUntil = Math.max(e.revealedUntil, s.tick + Math.round(0.5 * TICK_RATE));
  }

  // Doc 06's bite is written about WALLS — "ignores mazes: bites through walls
  // at 500 dmg/bite" — and that is exactly where it applies: one bite, one
  // wall, however thick the wall got. Everything else it eats at its stated
  // DPS. Applied to the dome instead, 500 every two seconds ends a 1250 HP
  // Core in five seconds, which is a coin flip rather than a boss fight.
  if (sp.bite && target.kind === 'wall') {
    if (s.tick < e.biteTick) return; // between bites: it is winding up
    e.biteTick = s.tick + Math.round(sp.bite.everySeconds * TICK_RATE);
    target.hp -= sp.bite.damage;
    host.emit({ type: 'bite', x: e.x, y: e.y, structureId: target.id });
    if (target.hp <= 0) host.destroyStructure(target);
    return;
  }

  const c = structCentre(target);
  const d = Math.sqrt((c.x - e.x) * (c.x - e.x) + (c.y - e.y) * (c.y - e.y));
  const melee = target.size / 2 + 0.7;
  if (sp.rangedDps > 0 && d > melee) {
    if (d > sp.rangedRange + 0.5) {
      e.attackTargetId = -1; // it wandered out of spitting distance
      return;
    }
    target.hp -= sp.rangedDps * DT;
    // one visible spit per second, not thirty
    if (s.tick % TICK_RATE === 0) host.emit({ type: 'spit', x: e.x, y: e.y, toX: c.x, toY: c.y });
  } else {
    target.hp -= e.dps * DT;
  }
  if (target.hp <= 0) host.destroyStructure(target);
}

/** The saboteur's shopping list: nearest live structure of its preferred class. */
function sabotageGoal(host: FaunaHost, e: Enemy, sp: SpeciesDef): Structure | undefined {
  const held = e.goalStructId >= 0 ? host.byId(e.goalStructId) : undefined;
  if (held && !held.ruin) return held;
  const wants = sp.saboteurTarget;
  let goal: Structure | undefined;
  let bestD = Infinity;
  for (const st of host.state.structures) {
    if (st.ruin) continue;
    const match =
      wants === 'building' ? st.kind === 'building' : wants === 'wall' ? st.kind === 'wall' : st.kind === 'tower';
    if (!match) continue;
    const c = structCentre(st);
    const d = (c.x - e.x) * (c.x - e.x) + (c.y - e.y) * (c.y - e.y);
    if (d < bestD) {
      bestD = d;
      goal = st;
    }
  }
  e.goalStructId = goal ? goal.id : -1;
  return goal;
}

/**
 * Walk straight at a chosen structure, chewing whatever stands in the way.
 * Returns true if it handled this enemy's turn — false falls back to the flow
 * field, which is what a saboteur does when the base holds nothing it wants.
 */
function pursue(host: FaunaHost, e: Enemy, sp: SpeciesDef, speed: number, goal: Structure | undefined): boolean {
  if (!goal) return false;
  const c = structCentre(goal);
  const dx = c.x - e.x;
  const dy = c.y - e.y;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  // an acid spitter stops where its acid reaches (doc 03)
  const reach = sp.rangedRange > 0 ? sp.rangedRange : goal.size / 2 + 0.7;
  if (len <= reach) {
    e.attackTargetId = goal.id;
    return true;
  }
  // probe the tile ahead: anything solid in the way gets eaten first
  const px = Math.round(e.x + (dx / len) * 0.8);
  const py = Math.round(e.y + (dy / len) * 0.8);
  if (host.grid.inBounds(px, py)) {
    if (host.grid.terrain[host.grid.idx(px, py)] === T_ROCK) return false; // detour via the flow field
    const occ = host.grid.occ[host.grid.idx(px, py)]!;
    const blocker = occ >= 0 ? host.byId(occ) : undefined;
    if (blocker && !blocker.ruin && blocker.id !== goal.id) {
      e.attackTargetId = blocker.id;
      return true;
    }
  }
  const step = speed * DT;
  e.x += (dx / len) * Math.min(step, len);
  e.y += (dy / len) * Math.min(step, len);
  return true;
}

/** The default walk: one step down the flow field, biting whatever blocks it. */
function walkFlowField(host: FaunaHost, e: Enemy, speed: number, n: number): void {
  const ti = Math.max(0, Math.min(n - 1, Math.round(e.x)));
  const tj = Math.max(0, Math.min(n - 1, Math.round(e.y)));
  const nextT = host.flow.next[host.grid.idx(ti, tj)]!;
  if (nextT < 0) return; // standing on the Core tile or sealed off — hold
  const ni = nextT % n;
  const nj = (nextT / n) | 0;
  const occ = host.grid.occ[nextT]!;
  const blocker = occ >= 0 ? host.byId(occ) : undefined;
  const blocked = blocker !== undefined && !blocker.ruin;
  // aim at the tile centre when biting a structure, lane offset when walking
  const tx = blocked ? ni : ni + e.laneX;
  const ty = blocked ? nj : nj + e.laneY;
  const dx = tx - e.x;
  const dy = ty - e.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (blocked && len < 1.0) {
    e.attackTargetId = blocker.id; // reached the wall — start chewing (doc 03)
    return;
  }
  const step = speed * DT;
  if (len <= step) {
    e.x = tx;
    e.y = ty;
  } else {
    e.x += (dx / len) * step;
    e.y += (dy / len) * step;
  }
}
